import React from 'react';
import styled, { keyframes } from 'styled-components';

interface VisualStep {
  id: number;
  name: string;
  status: 'completed' | 'active' | 'pending' | 'failed';
  showSpinner: boolean;
}

interface ProgressStepsProps {
  steps: VisualStep[];
}

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const ProgressContainer = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #30363d;
`;

const StepsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const StepWrapper = styled.div<{ $status: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 0 0 auto;
  z-index: 1;
`;

const StepCircle = styled.div<{ $status: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid;
  background-color: #0d1117;
  transition: all 0.3s ease;

  /* Pending - gray */
  border-color: ${props => props.$status === 'pending' ? '#30363d' :
                          props.$status === 'active' ? '#10b981' :
                          props.$status === 'completed' ? '#10b981' :
                          '#ef4444'};

  background-color: ${props => props.$status === 'pending' ? '#0d1117' :
                              props.$status === 'active' ? 'rgba(16, 185, 129, 0.1)' :
                              props.$status === 'completed' ? '#10b981' :
                              'rgba(239, 68, 68, 0.1)'};
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(16, 185, 129, 0.3);
  border-top-color: #10b981;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const CheckmarkSvg = styled.svg`
  width: 18px;
  height: 18px;
  fill: #0d1117;
`;

const XMarkSvg = styled.svg`
  width: 16px;
  height: 16px;
  fill: #ef4444;
`;

const StepLabel = styled.div<{ $status: string }>`
  margin-top: 8px;
  font-size: 10px;
  font-weight: ${props => props.$status === 'active' ? '600' : '500'};
  text-align: center;
  white-space: nowrap;
  color: ${props => props.$status === 'active' ? '#10b981' :
                    props.$status === 'completed' ? '#10b981' :
                    props.$status === 'failed' ? '#ef4444' :
                    '#6b7280'};
`;

const StepConnector = styled.div<{ $status: string }>`
  flex: 1;
  height: 2px;
  margin: 0 8px;
  margin-bottom: 28px;
  background-color: ${props => props.$status === 'completed' ? '#10b981' : '#30363d'};
`;

export const ProgressSteps: React.FC<ProgressStepsProps> = ({ steps }) => {
  return (
    <ProgressContainer>
      <StepsWrapper>
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <StepWrapper $status={step.status}>
              <StepCircle $status={step.status}>
                {step.showSpinner ? (
                  <Spinner />
                ) : step.status === 'completed' ? (
                  <CheckmarkSvg viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </CheckmarkSvg>
                ) : step.status === 'failed' ? (
                  <XMarkSvg viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </XMarkSvg>
                ) : null}
              </StepCircle>
              <StepLabel $status={step.status}>{step.name}</StepLabel>
            </StepWrapper>
            {index < steps.length - 1 && (
              <StepConnector $status={step.status === 'completed' ? 'completed' : 'pending'} />
            )}
          </React.Fragment>
        ))}
      </StepsWrapper>
    </ProgressContainer>
  );
};
